/**
 * Agent Service — agents, the agent team, A2A cards, and memory backend.
 *
 * Pattern (the service standard): one thin `callFunction(route, data)` call per
 * method — no per-method boilerplate. callFunction handles auth + unwraps the
 * { success, data } envelope, so methods return the data directly.
 */
import { callFunction } from '../utils/apiClient';
import { ProjectAgent, CreateAgentInput, ChatWithAgentInput, AgentChatResponse } from '../types/agent';

// ── Agents (team members) ───────────────────────────────────────────

export const createAgent = (input: CreateAgentInput) =>
  callFunction<ProjectAgent>('createProjectAgent', input);

export const getProjectAgents = (projectId: string) =>
  callFunction<ProjectAgent[]>('getProjectAgents', { projectId });

export const updateAgent = (agentId: string, updates: Partial<ProjectAgent>) =>
  callFunction<ProjectAgent>('updateProjectAgent', { agentId, updates });

export const deleteAgent = (agentId: string) =>
  callFunction<void>('deleteProjectAgent', { agentId });

export const chatWithAgent = (input: ChatWithAgentInput) =>
  callFunction<AgentChatResponse>('chatWithAgent', input);

// ── Agent team (profiles → roster) ──────────────────────────────────

/** A selectable agent persona from the catalog. */
export interface AgentProfile {
  id: string;
  name: string;
  persona: string;
  description: string;
  avatar_icon: string;
  model_tier: 'SMART' | 'EXPERT' | 'RESEARCH';
  temperature: number;
  capabilities: string[];
  /** Catalog domain for the picker's filter tabs. */
  domain?: 'markets' | 'creative' | 'marketing' | 'research' | 'engineering' | 'lifestyle'
    | 'startup' | 'datascience' | 'study' | 'media'
    | 'wellness' | 'finance' | 'legal' | 'career' | 'product' | 'cooking' | 'travel' | 'coaching';
  /** Expert frameworks (unique knowledge base) — surfaced on the public agent page. */
  frameworks?: string[];
  tools?: string[];
  isDefault: boolean;
  /** Ethnicity-hinting native given-name, shown alongside the role-name (or null). */
  native_name?: string | null;
}

export const getAgentProfiles = () =>
  callFunction<AgentProfile[]>('getAgentProfiles', {});

/** Public, crawlable catalog persona (no system prompt) — for the /agent/{slug} directory. */
export interface PublicAgent {
  id: string;
  name: string;
  persona: string;
  description: string;
  avatar_icon: string;
  domain: string | null;
  capabilities: string[];
  frameworks: string[];
  personality: string;
  skills: string[];
  backstory?: string | null;
  native_name?: string | null;
  /** Admin-set default avatar image (a Storage URL) for this catalog agent, if uploaded. */
  avatar_image?: string | null;
}

/** PUBLIC — no auth. The agent directory data behind /agent/{slug}. Must pass requireAuth:false so
 *  anonymous visitors (and the avatar overlay) can call it — the client otherwise throws
 *  "User not authenticated" before the request, even though the server allows this route publicly. */
export const getPublicAgents = () =>
  callFunction<{ agents: PublicAgent[] }>('getPublicAgents', {}, { requireAuth: false }).then((r) => r.agents || []);

// Cached profile_id -> admin default avatar image map. Fetched once per session; the in-chat
// avatar renderer uses it as the fallback below a per-chat user-generated avatar. A member with
// no per-chat avatar shows the admin default; if none, its Lucide icon.
let _catalogAvatarMap: Promise<Record<string, string>> | null = null;
export const getCatalogAvatarMap = (): Promise<Record<string, string>> => {
  if (!_catalogAvatarMap) {
    _catalogAvatarMap = getPublicAgents()
      .then((agents) => {
        const map: Record<string, string> = {};
        for (const a of agents) if (a.avatar_image) map[a.id] = a.avatar_image;
        return map;
      })
      .catch(() => ({}));
  }
  return _catalogAvatarMap;
};

/**
 * id -> the agent's OWN Lucide icon name, for every surface that shows a face.
 *
 * Needed because `avatar_image` is an ADMIN OVERLAY and the profile catalog defines none of them (89
 * `avatar_icon`s, zero `avatar_image`s as of 2026-08-30) — so a surface that falls back to one generic glyph
 * shows the same anonymous silhouette for every agent in most environments, which is exactly what "I can't see
 * the selected agent's face" looks like. The icon is the catalog's own answer to "who is this", and it is
 * always present.
 *
 * Shares `getPublicAgents`, which the service already caches, so this costs no extra round trip.
 */
let _catalogIconMap: Promise<Record<string, string>> | null = null;
export const getCatalogIconMap = (): Promise<Record<string, string>> => {
  if (!_catalogIconMap) {
    _catalogIconMap = getPublicAgents()
      .then((agents) => {
        const map: Record<string, string> = {};
        for (const a of agents) if (a.avatar_icon) map[a.id] = a.avatar_icon;
        return map;
      })
      .catch(() => ({}));
  }
  return _catalogIconMap;
};

/** The derived four-axis "character sheet" for an agent (0-5 scores). Mirrors the backend agentStats. */
export interface AgentStats {
  intelligence: number;
  knowledge: number;
  execution: number;
  powers: string[];
  personality: { archetype: string; mbti: string; humor: string; warmth: number; energy: number; wit: number; creativity: number };
  languages: { name: string; fluency: 'native' | 'fluent' | 'conversational' }[];
}

/** Read-only BIO + knowledgebase for the Agent-details card. Null for a custom agent (no catalog). */
export interface AgentBio {
  name: string;
  persona: string;
  description: string;
  native_name?: string | null;
  personality: string | null;
  appearance: string | null;
  style: string | null;
  hobby?: string | null;
  backstory?: string | null;
  capabilities: string[];
  // Internal data-model — admin-only (empty/null for regular users; see getAgentBio).
  skills: string[];
  knowledge: string[];
  stats?: AgentStats | null;
}
export const getAgentBio = (profileId: string) =>
  callFunction<AgentBio | null>('getAgentBio', { profileId });

/** A project's team is its roster of agents. */
export const getProjectTeam = getProjectAgents;

export const initializeProjectTeam = (projectId: string) =>
  callFunction<ProjectAgent[]>('initializeProjectTeam', { projectId });

export const addTeamMemberFromProfile = (
  projectId: string,
  profileId: string,
  overrides?: Partial<Pick<ProjectAgent, 'name' | 'system_prompt' | 'temperature'>>
) => callFunction<ProjectAgent>('addTeamMemberFromProfile', { projectId, profileId, overrides });

export const updateTeamMember = updateAgent;
export const removeTeamMember = deleteAgent;

// ── A2A discovery + memory backend ──────────────────────────────────

export type MemoryBackend = 'drive' | 'firestore';

export const getAgentCards = () => callFunction<any[]>('getAgentCards', {});

export const getAgentMemoryBackend = () =>
  callFunction<{ backend: MemoryBackend }>('getAgentMemoryBackend', {}).then((r) => r?.backend || 'firestore');

export const setAgentMemoryBackend = (backend: MemoryBackend) =>
  callFunction<{ backend: MemoryBackend }>('setAgentMemoryBackend', { backend }).then((r) => r?.backend || backend);
